import React from "react";
import { compose, withHandlers, withStateHandlers } from "recompose";
import { Trans } from "react-i18next";
import { Table, Button, PopoverBody } from "reactstrap";
import { Link } from "react-router-dom";
import {
  TableContainer,
  withTable,
  withPopover,
  PendingButton,
} from "components/common";
import { withXeroErrors, XeroTableDataFetch } from "components";
import { formatDate, formatCurrency } from "services/util";
import { NotificationManager } from "react-notifications";
import { chain } from "lodash";
import { XeroClient } from "services";

export const CurrentClaim = ({
  data,
  ordering,
  refreshToken,
  url = "receipts",
  filters,
  setData,
  setXeroErrors,
  setOrdering,
  selected,
  numSelected,
  setSelected,
  limit,
  offset,
  togglePopover,
  setOffset,
  pending,
  handleSubmitForApproval,
  Popover,
}) => (
  <div>
    <div className="d-flex justify-content-between mb-2">
      <Link to="/receipts/new" className="btn btn-primary">
        <span className="fa fa-plus" />
        <span className="mx-1" />
        <Trans>Add Receipt</Trans>
      </Link>
      <Button
        onClick={togglePopover}
        color="primary"
        id="submit-for-approval"
        disabled={!numSelected}
      >
        <Trans>Submit for Approval</Trans>
      </Button>
    </div>
    <Popover target="#submit-for-approval">
      <PopoverBody>
        <Trans>
          You have selected {{ numSelected }} item(s) to submit for approval.
        </Trans>
        <div className="d-flex justify-content-end mt-1">
          <PendingButton
            pending={pending}
            onClick={handleSubmitForApproval}
            color="success"
          >
            OK
          </PendingButton>
        </div>
      </PopoverBody>
    </Popover>
    <TableContainer
      idKey="ReceiptID"
      items={data.results}
      ordering={ordering}
      onOrderingChanged={setOrdering}
      selected={selected}
      onSelectedChanged={setSelected}
    >
      <Table size="sm">
        <thead>
          <tr>
            <th select />
            <th sortBy="ReceiptNumber">
              <Trans>Receipt</Trans>
            </th>
            <th sortBy="Contact.Name">
              <Trans>Receipt From</Trans>
            </th>
            <th sortBy="Date">
              <Trans>Receipt Date</Trans>
            </th>
            <th sortBy="Status">
              <Trans>Status</Trans>
            </th>
            <th sortBy="Total">
              <Trans>Amount</Trans>
            </th>
          </tr>
        </thead>
        <tbody>
          {data.results.map((i, idx) => (
            <tr key={i.ReceiptID}>
              <td select />
              <td>
                <Link to={`/receipts/${i.ReceiptID}`}>{i.ReceiptNumber}</Link>
              </td>
              <td>
                {!!i.Contact && (
                  <Link to={`/contacts/view/${i.Contact.ContactID}`}>
                    {i.Contact.Name}
                  </Link>
                )}
              </td>
              <td>{!!i.Date && formatDate(i.Date)}</td>
              <td>{i.Status}</td>
              <td>{formatCurrency(i.Total)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </TableContainer>
    <XeroTableDataFetch
      ordering={ordering}
      refreshToken={refreshToken}
      limit={limit}
      url={url}
      filters={filters}
      offset={offset}
      onDataFetched={setData}
      onDataError={setXeroErrors}
    />
  </div>
);

export const enhance = compose(
  withTable({
    limit: 100,
    idKey: "ReceiptID",
  }),
  withStateHandlers(
    () => ({
      xero: XeroClient(),
    }),
    {
      setPending: () => (pending) => ({ pending }),
    }
  ),
  withHandlers({
    handleSubmitForApproval:
      ({ selected, data, xero, setXeroErrors, refreshData, setPending }) =>
      () => {
        setPending(true);
        const ExpenseClaims = chain(data.results)
          .filter((i) => selected[i.ReceiptID])
          .groupBy((i) => i.User.UserID)
          .map((i, UserID) => ({
            User: {
              UserID,
            },
            Receipts: i.map(({ ReceiptID }) => ({
              ReceiptID,
            })),
          }))
          .value();

        xero
          .post("expenseclaims", ExpenseClaims)
          .then((resp) => {
            NotificationManager.success("Successfully submitted for approval");
            refreshData();
          }, setXeroErrors)
          .finally(() => setPending(false));
      },
  }),
  withXeroErrors(),
  withPopover()
);

export default enhance(CurrentClaim);
